import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard, MapPin, Users, Radio, TrainFront, AlertTriangle,
    Settings, Smartphone, LogOut, Menu, History, Sun, Moon, Wifi, Map, Building2,
    Network, Boxes, ChevronDown, Radar, Globe, Check, type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useUnreadCount } from '../../hooks/useAlerts';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { IDIOMAS, NOMBRES_IDIOMA } from '@/i18n';
import { useT } from '@/i18n/useT';

// Las entradas guardan la CLAVE de traducción, no el texto: el array es de módulo y
// se evalúa una sola vez, así que una etiqueta literal se quedaría congelada en el
// idioma inicial al cambiar el selector.
type NavItem = { to: string; clave: string; icon: LucideIcon; id?: string };
type NavGroup = { clave: string; icon: LucideIcon; items: NavItem[] };
type NavEntry = NavItem | NavGroup;

const isGroup = (e: NavEntry): e is NavGroup => 'items' in e;

/**
 * Navegación agrupada: 12 secciones planas no caben en la barra (se recortaban con el
 * scroll horizontal sin scrollbar), así que se pliegan en 3 desplegables + 3 accesos
 * directos. Dashboard va suelto por ser la entrada por defecto, y Alertas también
 * porque su badge de no leídas es crítico y dentro de un desplegable se perdería.
 */
const navEntries: NavEntry[] = [
    { to: '/dashboard', clave: 'comun.nav.dashboard', icon: LayoutDashboard },
    {
        clave: 'comun.nav.ubicaciones',
        icon: MapPin,
        items: [
            { to: '/posiciones', clave: 'comun.nav.posiciones', icon: MapPin },
            { to: '/interior',   clave: 'comun.nav.interior',   icon: Radar },
            { to: '/historico',  clave: 'comun.nav.historico',  icon: History },
            // La ruta y el modelo siguen siendo "planta"; solo cambia la etiqueta visible.
            { to: '/plantas',    clave: 'comun.nav.andenes',    icon: Map },
        ],
    },
    {
        clave: 'comun.nav.redes',
        icon: Network,
        items: [
            { to: '/beacons',    clave: 'comun.nav.beacons',   icon: Radio },
            { to: '/redes-wifi', clave: 'comun.nav.redesWifi', icon: Wifi },
        ],
    },
    {
        clave: 'comun.nav.gestion',
        icon: Boxes,
        items: [
            { to: '/agentes',      clave: 'comun.nav.agentes',      icon: Users },
            { to: '/dispositivos', clave: 'comun.nav.dispositivos', icon: Smartphone },
            { to: '/estaciones',   clave: 'comun.nav.estaciones',   icon: Building2 },
            { to: '/trenes',       clave: 'comun.nav.trenes',       icon: TrainFront },
        ],
    },
    { to: '/alertas',       clave: 'comun.nav.alertas',       icon: AlertTriangle, id: 'alertas' },
    { to: '/configuracion', clave: 'comun.nav.configuracion', icon: Settings },
];

/** Todas las secciones en plano, para el menú móvil. */
const flatNavItems: NavItem[] = navEntries.flatMap((e) => (isGroup(e) ? e.items : [e]));

function BrandMark() {
    // Icono real de la app (el puntero con el metro dentro). Es claro, así que va sobre un chip
    // azul marino —el color de marca del propio icono— para verse en barra clara y oscura.
    return (
        <span
            className="inline-flex items-center justify-center h-8 w-8 rounded-lg shrink-0"
            style={{ background: 'hsl(var(--brand-navy))' }}
        >
            <img
                src="/icons/logo.png"
                alt="MetroSafe"
                width={24}
                height={24}
                className="h-6 w-6 object-contain"
            />
        </span>
    );
}

function AlertBadge({ count }: { count: number }) {
    if (!count) return null;
    return (
        <span
            className="inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] text-[10px] font-semibold leading-[1.4]"
            style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
        >
            {count > 99 ? '99+' : count}
        </span>
    );
}

/** Subrayado rojo de sección activa, compartido por enlaces y disparadores de grupo. */
function ActiveUnderline() {
    return (
        <span
            aria-hidden
            className="absolute left-2 right-2 -bottom-[1px] h-[2px] rounded-sm"
            style={{ background: 'hsl(var(--primary))' }}
        />
    );
}

const desktopEntryClass = (active: boolean) =>
    cn(
        'relative inline-flex items-center gap-1.5 h-[38px] px-2.5 rounded-lg whitespace-nowrap',
        'text-[12.5px] transition-colors outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        active
            ? 'font-semibold text-foreground'
            : 'font-medium text-muted-foreground hover:text-foreground',
    );

function DesktopLink({ to, label, icon: Icon, badge }: { to: string; label: string; icon: LucideIcon; badge?: number }) {
    return (
        <NavLink to={to} className={({ isActive }) => desktopEntryClass(isActive)}>
            {({ isActive }) => (
                <>
                    <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>{label}</span>
                    {badge ? <AlertBadge count={badge} /> : null}
                    {isActive && <ActiveUnderline />}
                </>
            )}
        </NavLink>
    );
}

function DesktopGroup({ group }: { group: NavGroup }) {
    const t = useT();
    const { pathname } = useLocation();
    // El grupo se marca activo cuando la ruta actual es una de sus secciones, para no
    // perder la orientación al plegarlas en el desplegable.
    const active = group.items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
    const Icon = group.icon;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger className={cn(desktopEntryClass(active), 'group')}>
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{t(group.clave)}</span>
                <ChevronDown
                    className="h-3 w-3 shrink-0 opacity-60 transition-transform duration-150 group-data-[state=open]:rotate-180"
                    aria-hidden
                />
                {active && <ActiveUnderline />}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                {group.items.map(({ to, clave, icon: ItemIcon }) => (
                    <DropdownMenuItem key={to} asChild>
                        <NavLink
                            to={to}
                            className={({ isActive }) =>
                                // El activo se distingue por peso + barra de marca a la
                                // izquierda, no por color de texto: el hover ya usa bg-accent
                                // y el rojo como texto se confunde con una acción destructiva.
                                cn(
                                    'w-full',
                                    isActive &&
                                        'font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-primary'
                                )
                            }
                        >
                            <ItemIcon className="h-4 w-4 shrink-0" aria-hidden />
                            <span>{t(clave)}</span>
                        </NavLink>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function MobileMenu({ onNavigate, alertCount }: { onNavigate?: () => void; alertCount: number }) {
    const t = useT();
    return (
        <div className="h-full flex flex-col bg-card text-foreground">
            <div className="py-4 pl-5 pr-4 border-b border-border">
                <NavLink
                    to="/dashboard"
                    onClick={onNavigate}
                    aria-label={t('comun.marca.irAlDashboard')}
                    className="flex items-center gap-2 rounded-lg outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <BrandMark />
                    <div className="flex flex-col leading-tight">
                        <span className="text-base font-semibold tracking-tight">{t('comun.marca.nombre')}</span>
                        <span className="text-[10.5px] uppercase tracking-wider font-medium text-muted-foreground">
                            {t('comun.marca.sistema')}
                        </span>
                    </div>
                </NavLink>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
                {flatNavItems.map(({ to, clave, icon: Icon, id }) => (
                    <NavLink
                        key={to}
                        to={to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            cn(
                                'flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                                'relative',
                                isActive
                                    ? 'bg-accent text-foreground font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-primary'
                                    : 'text-foreground/70 hover:bg-muted hover:text-foreground'
                            )
                        }
                    >
                        <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                        <span className="flex-1">{t(clave)}</span>
                        {id === 'alertas' ? <AlertBadge count={alertCount} /> : null}
                    </NavLink>
                ))}
            </nav>
        </div>
    );
}

/**
 * Selector de idioma de la barra superior. El ajuste vive en Configuración, pero
 * tenerlo solo ahí lo deja prácticamente escondido: en la barra se ve el idioma
 * activo de un vistazo y se cambia en dos clics, sin salir de la pantalla.
 *
 * Muestra el código (ES, EU…) en vez de una bandera: una bandera no identifica un
 * idioma —el gallego, el catalán y el euskera no tienen la suya, y el portugués y
 * el inglés tienen varias—, y por debajo de sm se oculta y queda solo el icono.
 */
function LanguageMenu() {
    const t = useT();
    const { language, setLanguage } = useSettings();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                // Mismo aspecto que .ms-icon-btn pero de ancho variable: esa clase fija
                // 32px de ancho y aquí hay que dejar sitio al código de idioma.
                className={cn(
                    'inline-flex items-center gap-1 h-8 px-2 rounded-lg shrink-0',
                    'text-[11px] font-semibold uppercase tracking-wide',
                    'text-muted-foreground transition-colors outline-none',
                    'hover:bg-accent hover:text-foreground',
                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                )}
                title={t('comun.barra.cambiarIdioma')}
                aria-label={t('comun.barra.cambiarIdioma')}
            >
                <Globe className="h-4 w-4 shrink-0" aria-hidden />
                <span className="hidden sm:inline">{language}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {IDIOMAS.map((idioma) => (
                    <DropdownMenuItem
                        key={idioma}
                        onSelect={() => setLanguage(idioma)}
                        className="justify-between gap-6"
                    >
                        <span className={cn(idioma === language && 'font-semibold')}>
                            {NOMBRES_IDIOMA[idioma]}
                        </span>
                        {idioma === language && <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function Avatar({ name }: { name?: string | null }) {
    const initials = (name ?? '?')
        .split(' ')
        .map((s) => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    return (
        <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-semibold tracking-wide"
            style={{ background: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))' }}
        >
            {initials}
        </span>
    );
}

export default function Navbar() {
    const t = useT();
    const { user, logout } = useAuth();
    const { resolvedTheme, setTheme } = useSettings();
    const { data: alertCount = 0 } = useUnreadCount();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <header
            className="sticky top-0 z-sticky"
            style={{
                background: 'hsl(var(--topbar-bg))',
                borderBottom: '1px solid hsl(var(--topbar-border))',
                backdropFilter: 'saturate(140%) blur(8px)',
                WebkitBackdropFilter: 'saturate(140%) blur(8px)',
            }}
        >
            <div className="max-w-[1440px] mx-auto h-[60px] px-4 sm:px-6 flex items-center gap-3 sm:gap-4">
                {/* Mobile hamburger */}
                <Sheet open={open} onOpenChange={setOpen}>
                    <SheetTrigger asChild>
                        <button className="lg:hidden ms-icon-btn -ml-1" aria-label={t('comun.barra.abrirMenu')}>
                            <Menu className="h-4 w-4" aria-hidden />
                        </button>
                    </SheetTrigger>
                    <SheetContent side="left" className="p-0 w-64">
                        <MobileMenu onNavigate={() => setOpen(false)} alertCount={alertCount} />
                    </SheetContent>
                </Sheet>

                {/* Brand — atajo al Dashboard, convención habitual del logo en apps web */}
                <NavLink
                    to="/dashboard"
                    aria-label={t('comun.marca.irAlDashboard')}
                    className="flex items-center gap-2.5 shrink-0 rounded-lg -mx-1 px-1 py-0.5 outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                >
                    <BrandMark />
                    <div className="hidden sm:flex flex-col leading-[1.05]">
                        <span className="text-[15px] font-semibold tracking-tight text-foreground">{t('comun.marca.nombre')}</span>
                        <span className="text-[10.5px] uppercase tracking-wider font-medium text-muted-foreground">
                            {t('comun.marca.sistema')}
                        </span>
                    </div>
                </NavLink>

                {/* Divider */}
                <div className="hidden lg:block w-px h-[22px]" style={{ background: 'hsl(var(--border))' }} />

                {/* Desktop nav: 5 entradas, caben sin recortes desde lg (1024px) */}
                <nav className="hidden lg:flex items-center gap-0.5">
                    {navEntries.map((entry) =>
                        isGroup(entry) ? (
                            <DesktopGroup key={entry.clave} group={entry} />
                        ) : (
                            <DesktopLink
                                key={entry.to}
                                to={entry.to}
                                label={t(entry.clave)}
                                icon={entry.icon}
                                badge={entry.id === 'alertas' ? alertCount : undefined}
                            />
                        )
                    )}
                </nav>

                {/* Right cluster */}
                <div className="ml-auto flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <div className="hidden sm:flex items-center gap-1.5 text-[12px] text-muted-foreground" title={t('comun.barra.enVivo')}>
                        <span className="ms-live-dot" />
                        <span className="hidden xl:inline">{t('comun.barra.enVivo')}</span>
                    </div>

                    <LanguageMenu />

                    <button
                        type="button"
                        className="ms-icon-btn"
                        title={t('comun.barra.cambiarTema')}
                        onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                    >
                        {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
                    </button>

                    <div className="hidden sm:block w-px h-[22px] mx-1" style={{ background: 'hsl(var(--border))' }} />

                    <div className="hidden md:flex items-center gap-2">
                        <Avatar name={user?.name} />
                        <div className="hidden xl:block text-[12.5px] leading-[1.15]">
                            <div className="font-medium text-foreground">{user?.name ?? t('comun.barra.usuario')}</div>
                            <div className="text-[11px] text-muted-foreground">{user?.role ?? ''}</div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleLogout}
                        className="ms-icon-btn"
                        title={t('comun.barra.cerrarSesion')}
                        aria-label={t('comun.barra.cerrarSesion')}
                    >
                        <LogOut className="h-4 w-4" aria-hidden />
                    </button>
                </div>
            </div>
        </header>
    );
}
